import React, { useEffect, useState } from "react";
import { useAuth } from "../lib/auth";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Loader2, LogIn, Eye, EyeOff, Info, BarChart3 } from "lucide-react";
import api from "../lib/api";
import { useNotify } from "../lib/notify";

const DEFAULT_ORG = "Masjid Muslim Billionaire";
const DEFAULT_FOOTER = "@masjidmuslimbillionaire";
// Nilai bawaan lama di Pengaturan System — diganti otomatis dengan branding MMB
const LEGACY_NAMES = ["", "Emergent Laporan DM"];
const LEGACY_FOOTERS = ["", "© Emergent Reporting Portal"];

export default function LoginPage() {
  const { login, formatApiError } = useAuth();
  const notify = useNotify();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState({ yayasan_nama: "", logo_data: "", login_footer: "" });

  useEffect(() => {
    api.get("/public/settings").then((r) => setSettings(r.data || {})).catch(() => {});
  }, []);

  const org = LEGACY_NAMES.includes((settings.yayasan_nama || "").trim()) ? DEFAULT_ORG : settings.yayasan_nama.trim();
  const footer = LEGACY_FOOTERS.includes((settings.login_footer || "").trim()) ? DEFAULT_FOOTER : settings.login_footer.trim();

  const doLogin = async (e) => {
    e?.preventDefault?.();
    setLoading(true);
    try {
      await login(email, password);
      nav("/dashboard");
    } catch (err) {
      notify.error({
        title: "Login Gagal",
        description: formatApiError(err.response?.data?.detail) || "Email atau password tidak sesuai. Silakan coba lagi.",
      });
    } finally { setLoading(false); }
  };

  return (
    <div
      className="min-h-screen flex flex-col bg-gradient-to-b from-emerald-50/80 via-stone-50 to-amber-50/40"
      data-testid="login-page"
    >
      <main className="flex-1 flex items-start sm:items-center justify-center px-6 pt-24 pb-10 sm:py-16">
        <div className="w-full max-w-md">
          {/* Brand */}
          <div className="flex items-center gap-3.5" data-testid="login-brand">
            {settings.logo_data ? (
              <img src={settings.logo_data} alt={`Logo ${org}`} className="w-14 h-14 rounded-2xl object-cover bg-emerald-900 shadow-sm flex-shrink-0" />
            ) : (
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-800 to-emerald-950 shadow-sm flex items-center justify-center flex-shrink-0">
                <BarChart3 size={26} className="text-amber-300" />
              </div>
            )}
            <div className="min-w-0">
              <div className="text-[13px] sm:text-sm font-bold uppercase tracking-[0.22em] leading-snug text-emerald-900" data-testid="brand-title">
                Laporan Hasil Digital Marketing
              </div>
              <div className="text-sm font-semibold text-amber-600 mt-0.5 truncate" data-testid="brand-name">{org}</div>
            </div>
          </div>

          {/* Heading */}
          <h1 className="mt-14 text-2xl sm:text-[28px] font-extrabold tracking-tight text-slate-900" data-testid="login-heading">
            Masuk ke akun Anda
          </h1>
          <p className="mt-3 text-sm text-slate-500 leading-relaxed">
            Gunakan email beserta password dari Admin {org}.
          </p>

          {/* Form */}
          <form onSubmit={doLogin} className="mt-8 space-y-5" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-[15px] font-medium text-slate-800">Email</Label>
              <Input
                id="email" data-testid="input-login-email" type="email" placeholder="nama@email.com"
                className="h-12 mt-2 rounded-xl bg-white border-slate-200 text-[15px] shadow-sm placeholder:text-slate-400 focus-visible:ring-0 focus-visible:border-2 focus-visible:border-emerald-700"
                value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="username"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-[15px] font-medium text-slate-800">Password</Label>
              <div className="relative mt-2">
                <Input
                  id="password" data-testid="input-login-password" type={showPw ? "text" : "password"}
                  className="h-12 pr-12 rounded-xl bg-white border-slate-200 text-[15px] shadow-sm focus-visible:ring-0 focus-visible:border-2 focus-visible:border-emerald-700"
                  value={password} onChange={(e) => setPassword(e.target.value)} required autoComplete="current-password"
                />
                <button
                  type="button" onClick={() => setShowPw((v) => !v)}
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 w-10 h-10 rounded-lg flex items-center justify-center text-slate-400 hover:text-emerald-700 hover:bg-emerald-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600/40 transition-colors"
                  aria-label={showPw ? "Sembunyikan password" : "Tampilkan password"}
                  data-testid="btn-toggle-password"
                >
                  {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <Button
              type="submit" data-testid="btn-login-submit" disabled={loading}
              className="w-full h-12 rounded-xl bg-gradient-to-r from-emerald-800 to-emerald-600 hover:from-emerald-900 hover:to-emerald-700 text-white text-base font-semibold shadow-md shadow-emerald-900/15 active:scale-[0.99] transition-transform"
            >
              {loading ? <Loader2 className="animate-spin" size={18} /> : (<><LogIn size={18} className="mr-2.5" /> Masuk</>)}
            </Button>
          </form>

          {/* Info */}
          <div className="mt-7 flex items-start gap-3 rounded-2xl bg-sky-50/80 border border-sky-100 p-4 sm:p-5" data-testid="login-info">
            <Info size={18} className="text-slate-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-slate-600 leading-relaxed">
              Akses dikelola oleh Admin {org}.{" "}
              <b className="font-bold text-emerald-900">Lupa password atau akun dinonaktifkan?</b>{" "}
              Hubungi Admin untuk direset.
            </p>
          </div>
        </div>
      </main>

      <footer className="text-center text-xs text-slate-500 pb-8 px-4" data-testid="login-footer">{footer}</footer>
    </div>
  );
}
